import React, { useCallback, useEffect, useState } from 'react';
import smsgatewayService from '../../../../services/smsgateway.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { toast } from 'react-toastify';
import SubHeader from '../../../layout/components/SubHeader';
import OverviewCards from './widgets/OverviewCards';
import DailyChart from './widgets/DailyChart';
import MonthlyChart from './widgets/MonthlyChart';
import FailedMessagesPanel from './widgets/FailedMessagesPanel';
import clinicService from '../../../../services/clinic.service';
import SmsDashboardSkeleton from './widgets/SmsDashboardSkeleton';

function SmsDashboard() {
    const [dashboardOverview, setDashboardOverview] = useState(null);
    const [dailyChart, setDailyChart] = useState([]);
    const [monthlyChart, setMonthlyChart] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const setClinicInfo = useEyeconxStore((state) => state.setClinicInfo);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const setSmsInbox = useEyeconxStore((state) => state.setSmsInbox);

    const loadDashboard = useCallback(
        async (showRefreshToast = false) => {
            try {
                const dashboardResponse = await smsgatewayService.getDashboardData(clinicId);
                setDashboardOverview(dashboardResponse.data.overview);
                setDailyChart(dashboardResponse.data.dailyMessageChart || []);
                setMonthlyChart(dashboardResponse.data.monthlyMessageChart || []);
                setSmsInbox(parseInt(dashboardResponse.data.overview?.messagesUnread || 0, 10));

                const clinicResponse = await clinicService.getClinicInfo(clinicId);
                setClinicInfo(clinicResponse.data);

                if (showRefreshToast) {
                    toast.success('Dashboard updated');
                }
            } catch (err) {
                toast.error('Error loading dashboard data');
                console.error('Dashboard error:', err);
            }
        },
        [clinicId, setClinicInfo, setSmsInbox]
    );

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await loadDashboard(false);
            setIsLoading(false);
        };
        init();
    }, [loadDashboard]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await loadDashboard(true);
        setIsRefreshing(false);
    };

    const scrollToFailed = () => {
        document.getElementById('failed-messages-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (isLoading) {
        return <SmsDashboardSkeleton />;
    }

    return (
        <>
            <SubHeader
                title="Dashboard"
                description="SMS activity overview for the last 30 days"
                icon="bi-speedometer2"
                iconClass="text-primary"
                containerClass="container-fluid px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh dashboard"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="container-fluid px-4 py-3">
                {dashboardOverview && (
                    <OverviewCards data={dashboardOverview} onFailedClick={scrollToFailed} />
                )}

                <div className="row mt-4 g-4">
                    <div className="col-lg-8">
                        {dailyChart.length > 0 ? (
                            <DailyChart data={dailyChart} />
                        ) : (
                            <div className="card shadow-sm border-0">
                                <div className="card-body text-muted text-center py-5">
                                    <div className="bg-light rounded-circle p-4 d-inline-flex mb-3">
                                        <i className="bi bi-calendar-day text-primary fs-2"></i>
                                    </div>
                                    <p className="mb-0">No daily message activity in the last 30 days.</p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="col-lg-4">
                        {monthlyChart.length > 0 ? (
                            <MonthlyChart data={monthlyChart} />
                        ) : (
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body text-muted text-center py-5 d-flex flex-column justify-content-center">
                                    <div className="bg-light rounded-circle p-4 d-inline-flex mb-3 mx-auto">
                                        <i className="bi bi-bar-chart text-success fs-2"></i>
                                    </div>
                                    <p className="mb-0">No monthly data available yet.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <FailedMessagesPanel clinicId={clinicId} onResendSuccess={() => loadDashboard(false)} />
            </div>
        </>
    );
}

export default SmsDashboard;
